import { lazy, Suspense, useState } from "react";
import { Button, Form, Input, InputNumber, Modal, Select, Space, Spin, Switch, Tag, Tooltip } from "antd";
import { ImportOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { ListPage } from "../../components/ListPage";
import { useApi } from "../../helpers/useApi";
import { useItemCategoriesQuery, useSuppliersQuery, useTaxPoliciesQuery, useUnitsQuery } from "../../helpers/queries";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import type { Item } from "../../api-models";

const ItemImportDialog = lazy(() => import("./ItemImportDialog"));

// The Material Master's four Material_Type labels, plus the finer CONSUMABLE split the
// platform already relied on. Equipment is the master's name for what the tool-custody
// flows call TOOL.
const ITEM_TYPE_OPTIONS = [
  { value: "STOCK", label: "Stock item" },
  { value: "CONSUMABLE", label: "Consumable" },
  { value: "TOOL", label: "Equipment / reusable asset" },
  { value: "NON_STOCK", label: "Non-stock item" },
  { value: "SERVICE", label: "Service item" },
];

const PROCUREMENT_METHOD_OPTIONS = [
  { value: "CENTRAL_STOCK", label: "Central stock" },
  { value: "PURCHASE_ON_DEMAND", label: "Purchase on demand" },
  { value: "RENTAL", label: "Rental" },
];

const PROCUREMENT_METHOD_LABELS: Record<string, string> = Object.fromEntries(
  PROCUREMENT_METHOD_OPTIONS.map((o) => [o.value, o.label]),
);

const yesNo = (v: boolean) => <Tag color={v ? "blue" : "default"}>{v ? "Yes" : "No"}</Tag>;

export default function ItemsPage() {
  const { user } = useUser();
  const canWrite = hasPermission(user, "Items.Write");
  const { data: categories = [] } = useItemCategoriesQuery();
  const { data: units = [] } = useUnitsQuery();
  const { data: suppliers = [] } = useSuppliersQuery();
  const { data: taxPolicies = [] } = useTaxPoliciesQuery();
  const { request } = useApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Item | null>(null);
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form] = Form.useForm();
  const [reloadKey, setReloadKey] = useState(0);

  // Any write invalidates the cached material lists other pages read from (dropdowns, the
  // BOM importer's own matching data), not just this page's table.
  function refreshMaterials() {
    setReloadKey((k) => k + 1);
    queryClient.invalidateQueries({ queryKey: ["items"] });
    queryClient.invalidateQueries({ queryKey: ["item-categories"] });
  }

  function openCreate() {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  }

  function openEdit(m: Item) {
    setEditing(m);
    form.setFieldsValue(m);
    setOpen(true);
  }

  async function handleSubmit() {
    const values = await form.validateFields();
    if (editing) {
      await request({ method: "PUT", url: `/items/${editing.id}`, data: { ...editing, ...values } });
    } else {
      await request({ method: "POST", url: "/items", data: values });
    }
    setOpen(false);
    refreshMaterials();
  }

  return (
    <>
      <ListPage<Item>
        key={reloadKey}
        title="Material master"
        endpoint="/items"
        searchPlaceholder="Search materials..."
        actions={
          canWrite && (
            <Space>
              <Button icon={<ImportOutlined />} onClick={() => setImporting(true)}>Import</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} data-tour="items-new">
                New material
              </Button>
            </Space>
          )
        }
        columns={[
          { title: "Code", dataIndex: "code", sorter: true },
          {
            title: "Name",
            key: "name",
            sorter: true,
            render: (_, r) => (
              <Space size={4}>
                {r.name}
                {r.hazardous && (
                  <Tooltip title="Hazardous — special storage, transport and handling apply">
                    <WarningOutlined style={{ color: "#d4380d" }} />
                  </Tooltip>
                )}
              </Space>
            ),
          },
          { title: "Category", render: (_, r) => [r.category?.name, r.subCategory].filter(Boolean).join(" / ") },
          { title: "Type", key: "type", sorter: true, render: (_, r) => ITEM_TYPE_OPTIONS.find((o) => o.value === r.type)?.label ?? r.type },
          { title: "Base UOM", render: (_, r) => r.unit?.abbreviation },
          { title: "BOM eligible", dataIndex: "bomEligible", render: (v) => yesNo(v) },
          { title: "Stock managed", dataIndex: "stockControlled", render: (v) => yesNo(v) },
          { title: "Reorder level", dataIndex: "reorderLevel" },
          { title: "Reorder qty", dataIndex: "reorderQuantity" },
          {
            title: "Procurement",
            dataIndex: "procurementMethod",
            render: (v: string) => PROCUREMENT_METHOD_LABELS[v] ?? v,
          },
          { title: "QC", dataIndex: "requiresQualityInspection", render: (v) => yesNo(v) },
          // The rate, not just the policy's name: it is the number a buyer checks against
          // the order in front of them.
          { title: "Tax", render: (_, r) => (r.taxPolicy ? `${r.taxPolicy.name} (${r.taxPolicy.percentage}%)` : "-") },
          { title: "Status", dataIndex: "active", sorter: true, render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
          ...(canWrite ? [{ title: "", key: "edit", render: (_: unknown, r: Item) => <Button type="link" onClick={() => openEdit(r)}>Edit</Button> }] : []),
        ]}
      />
      <Modal
        title={editing ? `Edit ${editing.code}` : "New material"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSubmit}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          {/* Code is system-generated on create and immutable afterwards: it is the
              identifier every BOM line, stock balance and purchase document refers to. */}
          {editing && (
            <Form.Item label="Material code">
              <Input value={editing.code} disabled />
            </Form.Item>
          )}
          <Form.Item name="name" label="Material name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Specification notes" tooltip="Technical detail for engineering and procurement.">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="categoryId" label="Category">
            <Select options={categories.map((c) => ({ value: c.id, label: c.name }))} allowClear showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="subCategory" label="Subcategory">
            <Input />
          </Form.Item>
          <Form.Item name="type" label="Material type" initialValue="STOCK" rules={[{ required: true }]}>
            <Select options={ITEM_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="unitId" label="Base UOM" rules={[{ required: true }]}>
            <Select
              options={units.map((u) => ({ value: u.id, label: `${u.name} (${u.abbreviation})` }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            name="bomEligible"
            label="BOM eligible"
            valuePropName="checked"
            initialValue={true}
            tooltip="Whether the material can be added to a Project BOM."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="stockControlled"
            label="Stock managed"
            valuePropName="checked"
            initialValue={true}
            tooltip="Whether inventory quantities are tracked for this material."
          >
            <Switch />
          </Form.Item>
          <Form.Item name="reorderLevel" label="Reorder level" initialValue={0} tooltip="Minimum stock before replenishment is recommended.">
            <InputNumber min={0} step={0.001} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="reorderQuantity" label="Reorder quantity" initialValue={0} tooltip="Suggested quantity to purchase at the reorder level.">
            <InputNumber min={0} step={0.001} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="procurementMethod" label="Default procurement method" initialValue="PURCHASE_ON_DEMAND">
            <Select options={PROCUREMENT_METHOD_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="requiresQualityInspection"
            label="Requires quality inspection"
            valuePropName="checked"
            initialValue={false}
            tooltip="Must pass inspection before being issued or used."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="hazardous"
            label="Hazardous material"
            valuePropName="checked"
            initialValue={false}
            tooltip="Requires special storage, transport or handling."
          >
            <Switch />
          </Form.Item>
          <Form.Item name="minStockQty" label="Minimum stock quantity" initialValue={0}>
            <InputNumber min={0} step={0.001} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="toolReusable" label="Reusable asset (tool custody)" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item
            name="taxPolicyId"
            label="Tax policy"
            tooltip="Optional. A purchase order line for this material starts on this rate; the buyer can still change or remove it."
          >
            <Select
              options={taxPolicies.map((p) => ({ value: p.id, label: `${p.name} (${p.percentage}%)` }))}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="No tax"
            />
          </Form.Item>
          <Form.Item name="preferredSupplierId" label="Preferred supplier">
            <Select options={suppliers.map((s) => ({ value: s.id, label: s.name }))} allowClear showSearch optionFilterProp="label" />
          </Form.Item>
          {editing && (
            <Form.Item name="active" label="Active" valuePropName="checked" initialValue={true}
              tooltip="Only Active materials are available for new transactions.">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
      {importing && (
        <Suspense fallback={<Spin fullscreen />}>
          <ItemImportDialog
            onClose={() => setImporting(false)}
            onImported={() => {
              setImporting(false);
              refreshMaterials();
            }}
          />
        </Suspense>
      )}
    </>
  );
}
